import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Award, Users, Truck, Heart, ArrowRight } from 'lucide-react';

const LIFESTYLE_IMAGE = "https://images.unsplash.com/photo-1758523417921-0f4884c38481?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBraXRjaGVuJTIwbGlmZXN0eWxlJTIwZmFtaWx5fGVufDB8fHx8MTc3MDE2ODc1OHww&ixlib=rb-4.1.0&q=85";
const DETAIL_IMAGE = "https://images.unsplash.com/photo-1613533927320-eb765de8a01b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxraXRjaGVuJTIwY2FiaW5ldCUyMGhhcmR3YXJlJTIwZGV0YWlsfGVufDB8fHx8MTc3MDE2ODc2M3ww&ixlib=rb-4.1.0&q=85";

const VALUES = [
  {
    icon: Award,
    title: 'Uncompromising Quality',
    description: 'We use only the finest materials and hardware. Every cabinet is built to last with solid wood construction and premium finishes.'
  },
  {
    icon: Users,
    title: 'Customer-First Approach',
    description: 'From design consultation to installation support, our team is dedicated to making your kitchen renovation journey seamless.'
  },
  {
    icon: Truck,
    title: 'Reliable Delivery',
    description: 'We understand timing is crucial. Our efficient logistics ensure your cabinets arrive safely and on schedule.'
  },
  {
    icon: Heart,
    title: 'Passionate Craftspeople',
    description: 'Our team of skilled craftspeople takes pride in every cabinet we produce, ensuring attention to detail in every piece.'
  },
];

export default function AboutPage() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative py-24 bg-[#F9F7F2]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl animate-fade-in-up">
            <span className="text-[#8B5E3C] text-sm font-medium uppercase tracking-wider">Our Story</span>
            <h1 className="font-manrope text-4xl sm:text-5xl font-bold text-[#1A1A1A] mt-4 mb-6">
              Crafting Dream Kitchens
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Kitchen99.ca was founded with a simple mission: to make premium kitchen cabinets accessible to every homeowner. What started as a small family workshop has grown into a trusted name in kitchen cabinetry across Canada.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-6">
                Where Quality Meets Affordability
              </h2>
              <div className="space-y-4 text-[#4B5563] leading-relaxed">
                <p>
                  We believe that everyone deserves a beautiful kitchen. That's why we've worked tirelessly to streamline our manufacturing process, passing the savings directly to you without ever compromising on quality.
                </p>
                <p>
                  Our cabinets are crafted using solid hardwoods, premium plywood boxes, and high-quality hardware from leading manufacturers. Each cabinet goes through rigorous quality checks before leaving our facility.
                </p>
                <p>
                  From classic shaker styles to sleek modern designs, our diverse collection ensures you'll find the perfect cabinets to match your vision and lifestyle.
                </p>
              </div>
              <div className="mt-8">
                <Button asChild>
                  <Link to="/shop" data-testid="about-shop-btn">
                    Explore Our Collection
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in-up stagger-2">
              <img
                src={LIFESTYLE_IMAGE}
                alt="Modern kitchen with Kitchen99 cabinets"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#8B5E3C] hidden lg:flex items-center justify-center p-6">
                <p className="text-white text-center font-manrope font-semibold">
                  "Quality craftsmanship in every detail"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#F9F7F2]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
              Our Core Values
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto">
              These principles guide everything we do at Kitchen99
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {VALUES.map((value, index) => (
              <div
                key={value.title}
                className={`bg-white p-8 animate-fade-in-up stagger-${index + 1}`}
                data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <value.icon size={40} className="text-[#8B5E3C] mb-4" />
                <h3 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Craftsmanship */}
      <section className="py-20" data-testid="about-craftsmanship">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={DETAIL_IMAGE}
                alt="Cabinet hardware detail"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2 animate-fade-in-up">
              <span className="text-[#8B5E3C] text-sm font-medium uppercase tracking-wider">
                Attention to Detail
              </span>
              <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6">
                Built to Last
              </h2>
              <div className="space-y-4 text-[#4B5563] leading-relaxed">
                <p>
                  Every cabinet that leaves our workshop is built to withstand decades of daily use. We use dovetail drawer construction, soft-close hinges, and premium hardware to ensure lasting quality.
                </p>
                <p>
                  Our finishes are hand-applied in multiple coats, creating a durable surface that resists moisture, stains, and everyday wear. The result is cabinetry that looks beautiful for years to come.
                </p>
              </div>
              <div className="mt-8">
                <Button variant="outline" asChild>
                  <Link to="/quote" data-testid="about-quote-btn">
                    Get Your Free Quote
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#8B5E3C]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Kitchen Transformation?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Our design consultants are ready to help you plan your dream kitchen.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-[#1A1A1A] hover:bg-[#F9F7F2] px-8"
              asChild
            >
              <Link to="/quote">
                Start Free Design
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#8B5E3C] px-8"
              asChild
            >
              <Link to="/contact">
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
